/**
 * The sync-problems panel.
 *
 * A record that failed to reach head office enough times stops being retried
 * and sits here. The indicator in the corner says how many; this says what each
 * one is and why it stopped, and offers the two ways out.
 *
 * Try again is safe for anyone to press — every record carries its own id and
 * the cloud saves by it, so a re-send changes nothing; a passing failure goes
 * through and a genuinely unsendable one simply parks again. Dismiss throws a
 * record away for good, which loses data, so it is shown only to whoever may
 * change a shop's settings and asks before it acts.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { Button } from '../ui/Button.jsx';
import { Spinner } from '../ui/Spinner.jsx';

/** "order_item" -> "Order item", for a person rather than a schema. */
function label(entity) {
  const words = String(entity ?? 'record').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function SyncProblems({ onClose }) {
  const queryClient = useQueryClient();
  const can = useAuth((s) => s.can);
  const canDismiss = can('settings.update.settings');

  const stuck = useQuery({
    queryKey: ['sync', 'stuck'],
    queryFn: () => api.get('/sync/stuck'),
  });

  const entries = stuck.data?.entries ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['sync'] });

  const retry = useMutation({
    mutationFn: () => api.post('/sync/retry-stuck', {}),
    onSuccess: refresh,
  });

  const dismiss = useMutation({
    mutationFn: (id) => api.post('/sync/dismiss-stuck', { id }),
    onSuccess: refresh,
  });

  const busy = retry.isPending || dismiss.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Sync problems"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <div className="w-full max-w-lg rounded-2xl bg-surface p-5">
        <h2 className="font-semibold text-lg text-ink">Sync problems</h2>
        <p className="mt-1 text-sm text-ink-2">
          These records could not be sent to head office and have stopped trying on their own. The
          till is unaffected and everything else is still syncing.
        </p>

        {retry.isSuccess && (
          <p className="mt-3 rounded-lg bg-surface-3 px-3.5 py-2.5 text-sm text-ink-2">
            {retry.data?.requeued > 0
              ? `Put ${retry.data.requeued} back in the queue. Any that come back cannot be sent as they are — dismiss those, or tell support.`
              : 'Nothing needed requeuing.'}
          </p>
        )}

        {stuck.isLoading ? (
          <div className="mt-6 flex justify-center">
            <Spinner />
          </div>
        ) : entries.length === 0 ? (
          <p className="mt-4 rounded-lg bg-surface-3 px-3.5 py-3 text-sm text-ink-2">
            Nothing is stuck. You can close this.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-rule">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{label(entry.entity)}</p>
                  <p className="break-words text-xs text-ink-3">
                    {entry.last_error || 'No reason was recorded.'}
                  </p>
                </div>
                {canDismiss && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Throw this ${label(entry.entity).toLowerCase()} away for good? ` +
                            'It will never reach head office.'
                        )
                      ) {
                        dismiss.mutate(entry.id);
                      }
                    }}
                    className="shrink-0 text-xs text-ink-3 underline hover:text-bad"
                  >
                    Dismiss
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex gap-2">
          <Button variant="secondary" size="lg" onClick={onClose}>
            Close
          </Button>
          {entries.length > 0 && (
            <Button size="lg" className="flex-1" busy={retry.isPending} onClick={() => retry.mutate()}>
              Try again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
